// THE TILES OVER THE REAL MODEL (owner 2026-10-29: "the sign-off pages should load
// fragments in the equally distributed tiles, and make the tiles transparent"): the
// equal grid drawn as see-through boxes inside the model viewer Construction tracking
// uses (pages/Tracking/ModelStage, its `sceneLayer`), so the model's own fragments stream
// in underneath and the tiles colour them.
//
// The grid is in the viewer's own frame - model units (feet), Y up - the frame the
// element boxes it was cut from are stored in (back/utils/levelElevation). A tile
// (i, j, k) spans x = origin.x + i*edge, z = origin.z + j*edge (plan), y = origin.y + k*edge.
//
// Two instanced meshes: tiles that say something (signed off, or in a volume) in their
// colour, and the rest as a faint glass; one line mesh for the tile edges. Nothing here
// is ever raycast by the viewer (it only picks its own meshes), so element clicks still
// reach the model; a click that is not a drag also picks the nearest tile.
// pointer-space-exempt: the click becomes the canvas's own -1..1 coordinates (clientX over
// its rendered rect, both zoomed px), never a style.
import * as THREE from "three";

const STRONG_OPACITY = 0.3;
const WEAK_OPACITY = 0.07;
const GAP = 0.97;

/** Where a tile sits, in model units: its centre and edge. */
export function tileCentre(grid, t) {
  return new THREE.Vector3(
    grid.origin.x + (t.i + 0.5) * grid.edge,
    grid.origin.y + (t.k + 0.5) * grid.edge,
    grid.origin.z + (t.j + 0.5) * grid.edge,
  );
}

/** The box around some tiles, in model units. */
export function tilesBox(grid, list) {
  const box = new THREE.Box3();
  for (const t of list) {
    box.expandByPoint(new THREE.Vector3(grid.origin.x + t.i * grid.edge, grid.origin.y + t.k * grid.edge, grid.origin.z + t.j * grid.edge));
    box.expandByPoint(new THREE.Vector3(grid.origin.x + (t.i + 1) * grid.edge, grid.origin.y + (t.k + 1) * grid.edge, grid.origin.z + (t.j + 1) * grid.edge));
  }
  return box;
}

/** A grid bubble: the label in a circle, drawn on a canvas, as a sprite that faces the camera. */
function bubbleSprite(label, size) {
  const c = document.createElement("canvas");
  c.width = 64; c.height = 64;
  const g = c.getContext("2d");
  g.fillStyle = "#ffffff"; g.strokeStyle = "#4b5563"; g.lineWidth = 4;
  g.beginPath(); g.arc(32, 32, 27, 0, Math.PI * 2); g.fill(); g.stroke();
  g.fillStyle = "#111827"; g.font = "bold 26px Arial"; g.textAlign = "center"; g.textBaseline = "middle";
  g.fillText(String(label || "").slice(0, 4), 32, 34);
  const tex = new THREE.CanvasTexture(c);
  // a fixed size on screen: sized in the world, the bubbles near the camera filled the picture
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true, sizeAttenuation: false }));
  sprite.scale.set(size, size, 1);
  sprite.renderOrder = 13;
  return sprite;
}

/**
 * THE BUILDING GRID (owner 2026-11-01: "the exported grid from the NWD/NWC/Revit should be
 * present on all 3D, section, tile view"): each line { label, start: [x, y, z], end } on the
 * ground under the tiles, with a post at each end carrying its bubble - so it reads in 3D, in
 * plan and in a section. (A second line over the top of every grid line made the picture a
 * mesh of dashes on staging.)
 */
/** A plan segment cut to a box's x/z extent (Liang-Barsky); null when it misses the box. */
export function clipToBox(a, b, box) {
  let t0 = 0; let t1 = 1;
  const dx = b[0] - a[0]; const dz = b[2] - a[2];
  const edges = [[-dx, a[0] - box.min.x], [dx, box.max.x - a[0]], [-dz, a[2] - box.min.z], [dz, box.max.z - a[2]]];
  for (const [p, q] of edges) {
    if (p === 0) { if (q < 0) return null; continue; }
    const r = q / p;
    if (p < 0) { if (r > t1) return null; if (r > t0) t0 = r; } else { if (r < t0) return null; if (r < t1) t1 = r; }
  }
  return [[a[0] + t0 * dx, a[1], a[2] + t0 * dz], [a[0] + t1 * dx, a[1], a[2] + t1 * dz]];
}

// `clip`: a THREE.Box3 - each line cut to it, its bubbles where it leaves the box and the
// posts as tall as the box (the sign-off views, which show only the signed-off tiles)
export function gridLinesGroup(lines, grid, { clip = null } = {}) {
  const group = new THREE.Group();
  group.name = "signoff:grid";
  const raw = (lines || []).filter((l) => l && l.start && l.end);
  const list = clip ? raw.map((l) => { const c = clipToBox(l.start, l.end, clip); return c ? { label: l.label, start: c[0], end: c[1] } : null; }).filter(Boolean) : raw;
  if (!list.length || !grid || !grid.edge) return group;
  const lo = clip ? clip.min.y : grid.origin.y;
  const hi = clip ? clip.max.y : grid.origin.y + Math.max(grid.ny || 1, 1) * grid.edge;
  const pos = new Float32Array(list.length * 6 * 3);
  let o = 0;
  const put = (x, y, z) => { pos[o++] = x; pos[o++] = y; pos[o++] = z; };
  for (const l of list) {
    const [sx, , sz] = l.start; const [ex, , ez] = l.end;
    put(sx, lo, sz); put(ex, lo, ez);
    put(sx, lo, sz); put(sx, hi, sz);
    put(ex, lo, ez); put(ex, hi, ez);
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  // thin and faint: a site's grid is dozens of lines; dashed and dark they made a mesh
  const lineMat = new THREE.LineBasicMaterial({ color: 0x6b7280, transparent: true, opacity: 0.35, depthWrite: false });
  const segs = new THREE.LineSegments(geom, lineMat);
  segs.renderOrder = 11;
  segs.frustumCulled = false;
  group.add(segs);
  const size = 0.035; // of the view's height (sizeAttenuation off)
  for (const l of list) {
    for (const p of [l.start, l.end]) {
      const b = bubbleSprite(l.label, size);
      // inside the box when the picture is cut to it, above the tiles otherwise
      b.position.set(p[0], clip ? hi - grid.edge * 0.15 : hi + grid.edge * 0.5, p[2]);
      group.add(b);
    }
  }
  return group;
}

function disposeGroup(group) {
  group.traverse((o) => {
    if (o.geometry) o.geometry.dispose();
    if (o.material) { if (o.material.map) o.material.map.dispose(); o.material.dispose(); }
  });
}

/**
 * The layer, added to `viewer.scene`. `tiles` = the tile set's view ({ grid, tiles }).
 * Returns { paint(styleOf, visible), outline(keys, color), select(key), frame(keys), setGrid(lines), dispose }.
 *   styleOf(t) -> { color: "#rrggbb", strong: bool }
 */
export function createTileLayer(viewer, tiles, { onPick } = {}) {
  const grid = tiles.grid;
  const list = tiles.tiles || [];
  const n = Math.max(list.length, 1);
  const scene = viewer.scene;
  const group = new THREE.Group();
  group.name = "signoff:tiles";
  group.renderOrder = 10;
  // The depth-aware drawing (viewer/composite/surfaceComposite) redraws the scene with
  // one solid material and leaves out only what is marked a clearance shell; unmarked,
  // every see-through tile was drawn there as a solid box, so the model showed tile
  // outlines and shade through it, redrawn each time the camera settled (2026-10-30).
  group.userData.isClearance = true;
  scene.add(group);

  const geom = new THREE.BoxGeometry(grid.edge * GAP, grid.edge * GAP, grid.edge * GAP);
  const mk = (opacity) => {
    const mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide });
    const mesh = new THREE.InstancedMesh(geom, mat, n);
    mesh.renderOrder = 10;
    mesh.frustumCulled = false;
    const zero = new THREE.Matrix4().makeScale(0, 0, 0);
    for (let i = 0; i < n; i++) mesh.setMatrixAt(i, zero);
    const c = new THREE.Color("#ffffff");
    for (let i = 0; i < n; i++) mesh.setColorAt(i, c);
    group.add(mesh);
    return mesh;
  };
  const strong = mk(STRONG_OPACITY);
  const weak = mk(WEAK_OPACITY);
  const edgesMat = new THREE.LineBasicMaterial({ color: 0x64748b, transparent: true, opacity: 0.35, depthWrite: false });
  const edges = new THREE.LineSegments(new THREE.BufferGeometry(), edgesMat);
  edges.renderOrder = 11;
  edges.frustumCulled = false;
  group.add(edges);
  const marks = new THREE.Group(); // outlines of highlighted / selected tiles
  marks.renderOrder = 12;
  group.add(marks);
  const unit = new THREE.EdgesGeometry(new THREE.BoxGeometry(grid.edge, grid.edge, grid.edge));
  let gridGroup = null; // the building grid, when the project has one

  const state = { shown: new Array(list.length).fill(false), strong: new Array(list.length).fill(false), color: new Array(list.length).fill("") };
  // a colour change fades over FADE_MS (owner 2026-11-01: the timeline replay "fades /
  // transitions" from one sign-off to the next) rather than jumping
  const FADE_MS = 450;
  const fading = new Map(); // tile index -> { from: Color, to: Color }
  let fadeStart = 0;
  let fadeRaf = 0;
  function fadeStep() {
    const k = Math.min(1, (performance.now() - fadeStart) / FADE_MS);
    const c = new THREE.Color();
    for (const [i, f] of fading) {
      c.copy(f.from).lerp(f.to, k);
      strong.setColorAt(i, c);
      weak.setColorAt(i, c);
    }
    if (strong.instanceColor) strong.instanceColor.needsUpdate = true;
    if (weak.instanceColor) weak.instanceColor.needsUpdate = true;
    render();
    if (k < 1) fadeRaf = requestAnimationFrame(fadeStep);
    else { fading.clear(); fadeRaf = 0; }
  }
  const render = () => { if (typeof viewer.requestRender === "function") viewer.requestRender(0); };

  function edgeGeometry(idx) {
    // 12 edges per visible tile; the shared faces drawn twice cost nothing worth saving
    const pos = new Float32Array(idx.length * 24 * 3);
    let o = 0;
    const e = grid.edge * GAP / 2;
    const corners = [[-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1], [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1]];
    const pairs = [[0, 1], [1, 2], [2, 3], [3, 0], [4, 5], [5, 6], [6, 7], [7, 4], [0, 4], [1, 5], [2, 6], [3, 7]];
    for (const i of idx) {
      const c = tileCentre(grid, list[i]);
      for (const [a, b] of pairs) {
        for (const p of [corners[a], corners[b]]) {
          pos[o++] = c.x + p[0] * e; pos[o++] = c.y + p[1] * e; pos[o++] = c.z + p[2] * e;
        }
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return g;
  }

  /** Colour and show the tiles; only what changed is written. */
  function paint(styleOf, visible) {
    const m = new THREE.Matrix4();
    const zero = new THREE.Matrix4().makeScale(0, 0, 0);
    const col = new THREE.Color();
    let dirtyShown = false;
    let fadeNeeded = false;
    list.forEach((t, i) => {
      const show = visible ? !!visible(t) : true;
      const s = show ? styleOf(t) : null;
      const isStrong = !!(s && s.strong);
      const hex = s ? s.color : "";
      if (show !== state.shown[i] || isStrong !== state.strong[i]) {
        const c = tileCentre(grid, t);
        m.makeTranslation(c.x, c.y, c.z);
        strong.setMatrixAt(i, show && isStrong ? m : zero);
        weak.setMatrixAt(i, show && !isStrong ? m : zero);
        if (show !== state.shown[i]) dirtyShown = true;
        state.shown[i] = show;
        state.strong[i] = isStrong;
        strong.instanceMatrix.needsUpdate = true;
        weak.instanceMatrix.needsUpdate = true;
      }
      if (show && hex !== state.color[i]) {
        col.set(hex);
        if (state.color[i] && state.shown[i]) {
          // fade from where it is now (mid-fade included) to the new colour
          const from = new THREE.Color();
          const cur = fading.get(i);
          if (cur) { from.copy(cur.from).lerp(cur.to, Math.min(1, (performance.now() - fadeStart) / FADE_MS)); } else from.set(state.color[i]);
          fading.set(i, { from, to: col.clone() });
          fadeNeeded = true;
        } else {
          strong.setColorAt(i, col);
          weak.setColorAt(i, col);
          if (strong.instanceColor) strong.instanceColor.needsUpdate = true;
          if (weak.instanceColor) weak.instanceColor.needsUpdate = true;
        }
        state.color[i] = hex;
      }
    });
    // three keeps an instanced mesh's bounds from its first raycast and tests clicks against
    // them; a tile moved between the two meshes afterwards (tagged, signed off) fell outside
    // those bounds and could never be clicked again. Dropped here, recomputed on next use.
    strong.boundingSphere = null; strong.boundingBox = null;
    weak.boundingSphere = null; weak.boundingBox = null;
    if (dirtyShown) {
      edges.geometry.dispose();
      edges.geometry = edgeGeometry(list.map((_, i) => i).filter((i) => state.shown[i]));
    }
    if (fadeNeeded) {
      fadeStart = performance.now();
      if (!fadeRaf) fadeRaf = requestAnimationFrame(fadeStep);
    }
    render();
  }

  function clearMarks() {
    while (marks.children.length) {
      const o = marks.children.pop();
      if (o.material) o.material.dispose();
    }
  }
  let outlined = { keys: null, color: 0xe8590c };
  let selected = "";
  function drawMarks() {
    clearMarks();
    const add = (t, color) => {
      const line = new THREE.LineSegments(unit, new THREE.LineBasicMaterial({ color, depthTest: false, transparent: true }));
      line.renderOrder = 12;
      line.position.copy(tileCentre(grid, t));
      marks.add(line);
    };
    const keys = outlined.keys;
    if (keys && keys.size && keys.size < 4000) list.forEach((t, i) => { if (keys.has(t.key) && state.shown[i]) add(t, outlined.color); });
    const sel = selected && list.find((t) => t.key === selected);
    if (sel) add(sel, 0x111827);
    render();
  }

  // a click (not a drag) picks the nearest shown tile under it
  const dom = viewer.renderer && viewer.renderer.domElement;
  const ray = new THREE.Raycaster();
  let down = null;
  const onDown = (e) => { down = { x: e.clientX, y: e.clientY }; };
  const onUp = (e) => {
    if (!down || !onPick || Math.hypot(e.clientX - down.x, e.clientY - down.y) > 4) return;
    const r = dom.getBoundingClientRect();
    ray.setFromCamera(new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1), viewer.camera);
    const hits = [...ray.intersectObject(strong, false), ...ray.intersectObject(weak, false)]
      .filter((h) => state.shown[h.instanceId]).sort((a, b) => a.distance - b.distance);
    if (hits.length) onPick(list[hits[0].instanceId], e);
  };
  if (dom) {
    dom.addEventListener("pointerdown", onDown);
    dom.addEventListener("pointerup", onUp);
  }

  return {
    paint,
    /** The building grid lines ({ label, start, end } in the model frame); [] takes them off. */
    setGrid(lines, { clip = null } = {}) {
      if (gridGroup) { group.remove(gridGroup); disposeGroup(gridGroup); gridGroup = null; }
      if (lines && lines.length) { gridGroup = gridLinesGroup(lines, grid, { clip }); group.add(gridGroup); }
      render();
    },
    outline(keys, color = 0xe8590c) { outlined = { keys, color }; drawMarks(); },
    select(key) { selected = key || ""; drawMarks(); },
    /** Frame the camera on these tiles (all shown ones when none given). */
    frame(keys) {
      const pick = keys && keys.length ? list.filter((t) => keys.includes(t.key)) : list.filter((_, i) => state.shown[i]);
      if (!pick.length || !viewer.cameraControls) return;
      const box = tilesBox(grid, pick);
      try { viewer.cameraControls.fitToBox(box, true); } catch (e) { /* framing is best effort */ }
      render();
    },
    dispose() {
      if (dom) {
        dom.removeEventListener("pointerdown", onDown);
        dom.removeEventListener("pointerup", onUp);
      }
      clearMarks();
      if (fadeRaf) cancelAnimationFrame(fadeRaf);
      if (gridGroup) { disposeGroup(gridGroup); gridGroup = null; }
      scene.remove(group);
      geom.dispose(); unit.dispose(); edges.geometry.dispose(); edgesMat.dispose();
      strong.material.dispose(); weak.material.dispose(); strong.dispose(); weak.dispose();
      render();
    },
  };
}
